import React, { useState } from 'react';
import { X, Upload, Star, Package2, Camera, Loader2 } from 'lucide-react';
import config from '../config';
import { getChatRequestUrl } from '../config';
import { useAuth } from '../hooks/useAuth';
import { PRODUCT_CATEGORIES } from '../constants/productCategories';

const AddProductForm = ({ product, onSave, onCancel, targetCategory = 'current', customLists = [] }) => {
  const { getAuthSession } = useAuth();
  const [formData, setFormData] = useState({
    name: product?.name || '',
    brand: product?.brand || '',
    category: product?.category || '',
    categoryOther: '',
    listCategory: product?.listCategory || targetCategory,
    notes: product?.notes || '',
    rating: product?.rating || null,
    image: product?.image || product?.image_base64 || null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [errors, setErrors] = useState({});

  const categories = PRODUCT_CATEGORIES;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // First convert to base64 for preview and AI analysis
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target.result;
        
          // Auto-analyze image to extract product details (uses base64)
        await analyzeProductImage(imageData);
      };
      reader.readAsDataURL(file);
        
        // Upload to Supabase Storage to get a permanent URL
        const { userId } = await getAuthSession();
        const { supabase } = await import('../supabase');
        
        const filePath = `uploaded_images/${userId}/${Date.now()}_${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) throw uploadError;
        
        // Get public URL (never expires, bucket is public)
        const { data } = supabase.storage
          .from('images')
          .getPublicUrl(filePath);
        
        // Store the public URL
        setFormData(prev => ({ ...prev, image: data.publicUrl }));
        
      } catch (error) {
        console.error('Error uploading image:', error);
        setErrors(prev => ({
          ...prev,
          image: 'Failed to upload image. Please try again.'
        }));
      }
    }
  };

  const analyzeProductImage = async (imageData) => {
    setIsAnalyzingImage(true);
    
    try {
      // Get fresh authentication token
      const { tokens } = await getAuthSession();
      const accessToken = tokens?.accessToken;
      
      if (!accessToken) {
        throw new Error('Authentication required. Please refresh the page and sign in again.');
      }
      
      // Use getChatRequestUrl() — prefers Function URL (no 29s API Gateway
      // cap). Product extraction can blow the 29s window on cold starts.
      const API_URL = getChatRequestUrl();
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          extractProductDetails: true,
          image: imageData,
          userId: (await getAuthSession()).userId
        })
      });

      if (response.status === 401) {
        throw new Error('Session expired. Please refresh the page and sign in again.');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error response:', response.status, errorText);
        throw new Error(`Failed to analyze product image: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Product analysis result:', result);
      
      if (result.success && result.productDetails) {
        const details = result.productDetails;
        
        // Normalize category to match options
        let normalizedCategory = details.category || '';
        const categoryMapping = {
          'skincare': 'Skincare',
          'makeup': 'Makeup',
          'hair care': 'Hair Care',
          'body care': 'Body Care',
          'fragrance': 'Fragrance',
          'tools & accessories': 'Tools & Accessories'
        };
        normalizedCategory = categoryMapping[normalizedCategory.toLowerCase()] || normalizedCategory;
        
        // Auto-fill form with extracted details
        setFormData(prev => ({
          ...prev,
          name: details.name || prev.name,
          brand: details.brand || prev.brand,
          category: normalizedCategory || prev.category
        }));
        
        console.log('✅ Form auto-filled with:', {
          name: details.name,
          brand: details.brand,
          category: normalizedCategory
        });
      } else {
        console.warn('⚠️ No product details returned from backend');
      }
    } catch (error) {
      console.error('❌ Error analyzing product image:', error);
      // Show error to user instead of silently failing
      setErrors(prev => ({
        ...prev,
        image: 'Could not auto-fill from image. Please enter details manually.'
      }));
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.brand.trim()) newErrors.brand = 'Brand is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.listCategory) newErrors.listCategory = 'List selection is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const categoryValue = formData.category === 'Other' && formData.categoryOther.trim()
        ? formData.categoryOther.trim()
        : formData.category;
      const product = {
        name: formData.name.trim(),
        brand: formData.brand.trim(),
        category: categoryValue,
        listCategory: formData.listCategory,
        notes: formData.notes.trim(),
        rating: formData.rating,
        image: formData.image,
        savedAt: new Date().toISOString()
      };
      
      await onSave(product);
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleInputChange('rating', star)}
            className={`transition-colors ${
              star <= (formData.rating || 0) 
                ? 'text-yellow-400' 
                : 'text-gray-300 hover:text-yellow-300'
            }`}
          >
            <Star className="h-4 w-4 fill-current" />
          </button>
        ))}
        {formData.rating && (
          <span className="text-xs text-[#2A324B]/70 ml-1">
            {formData.rating}/5
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#F3D5B5] rounded-lg flex items-center justify-center">
              <Package2 className="h-4 w-4 text-[#2A324B]" />
            </div>
            <h3 className="text-lg font-semibold text-[#2A324B]">{product ? 'Edit Product' : 'Add Product'}</h3>
          </div>
          <button
            onClick={onCancel}
            className="text-[#2A324B]/50 hover:text-[#2A324B] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Product Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Vitamin C Serum"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] ${
                errors.name ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          {/* Brand */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Brand *
            </label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => handleInputChange('brand', e.target.value)}
              placeholder="e.g., The Ordinary"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] ${
                errors.brand ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.brand && (
              <p className="text-red-500 text-xs mt-1">{errors.brand}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] ${
                errors.category ? 'border-red-300' : 'border-gray-200'
              }`}
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-500 text-xs mt-1">{errors.category}</p>
            )}
            {formData.category === 'Other' && (
              <input
                type="text"
                value={formData.categoryOther}
                onChange={(e) => handleInputChange('categoryOther', e.target.value)}
                placeholder="Specify (e.g. Supplements)"
                className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5]"
              />
            )}
          </div>

          {/* List Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Save to List *
            </label>
            <select
              value={formData.listCategory || targetCategory}
              onChange={(e) => handleInputChange('listCategory', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] ${
                errors.listCategory ? 'border-red-300' : 'border-gray-200'
              }`}
            >
              <option value="current">Current products</option>
              <option value="wishlist">Wishlist</option>
              {customLists.map(list => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
            {errors.listCategory && (
              <p className="text-red-500 text-xs mt-1">{errors.listCategory}</p>
            )}
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Your Rating (Optional)
            </label>
            {renderStars()}
          </div>

          {/* Image Upload with AI Analysis */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Product Image {isAnalyzingImage && <span className="text-[#F3D5B5]">(Analyzing...)</span>}
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageUpload}
                className="hidden"
                id="camera-upload"
                disabled={isAnalyzingImage}
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={isAnalyzingImage}
              />
              <label
                htmlFor="camera-upload"
                className={`flex items-center gap-2 px-3 py-2 border border-[#F3D5B5] bg-[#F3D5B5]/20 rounded-lg hover:bg-[#F3D5B5]/40 cursor-pointer transition-colors ${isAnalyzingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Camera className="h-4 w-4 text-[#2A324B]" />
                <span className="text-sm text-[#2A324B] font-medium">Take Photo</span>
              </label>
              <label
                htmlFor="image-upload"
                className={`flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-[#F3D5B5]/20 cursor-pointer transition-colors ${isAnalyzingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Upload className="h-4 w-4 text-[#2A324B]" />
                <span className="text-sm text-[#2A324B]">Upload</span>
              </label>
              {isAnalyzingImage && (
                <div className="flex items-center gap-2 text-xs text-[#2A324B]/70">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Extracting product details...</span>
                </div>
              )}
            </div>
            {errors.image && (
              <p className="text-red-500 text-xs mt-2">{errors.image}</p>
            )}
            {formData.image && !isAnalyzingImage && (
              <div className="mt-2 flex items-center gap-2">
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-16 h-16 rounded object-cover border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => handleInputChange('image', null)}
                  className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Remove
                </button>
              </div>
            )}
            <p className="text-xs text-[#2A324B]/60 mt-1">
              Take a photo of the product, and we'll try to auto-fill the details
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2A324B] mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional notes about this product..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-[#341C1C]/20 rounded-lg text-[#2A324B] hover:bg-[#F3D5B5]/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-[#F3D5B5] text-[#2A324B] rounded-lg hover:bg-[#F3D5B5]/80 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (product ? 'Update Product' : 'Save Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductForm;
